import React from 'react';
import {
  Modal,
  Descriptions,
  Tag,
  Rate,
  Progress,
  Space,
  Typography,
  List,
  Card,
  Row,
  Col,
  Divider,
} from 'antd';
import {
  TrophyOutlined,
  WarningOutlined,
  ClockCircleOutlined,
  FireOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { Exercise, MuscleGroup, EquipmentReference } from '../../types';

const { Title, Text } = Typography;

interface ExerciseDetailsModalProps {
  visible: boolean;
  exercise: Exercise | null;
  muscleGroups: MuscleGroup[];
  equipment: EquipmentReference[];
  onClose: () => void;
}

const ExerciseDetailsModal: React.FC<ExerciseDetailsModalProps> = ({
  visible,
  exercise,
  muscleGroups,
  equipment,
  onClose,
}) => {
  if (!exercise) return null;

  const getMuscleGroupName = (id: string) => {
    const muscleGroup = muscleGroups.find(mg => mg.id === id);
    return muscleGroup?.name || id;
  };

  const getEquipmentName = (id: string) => {
    const eq = equipment.find(e => e.id === id);
    return eq?.name || id;
  };

  const getDifficultyColor = (difficulty: number) => {
    if (difficulty <= 3) return '#52c41a';
    if (difficulty <= 6) return '#faad14';
    return '#f5222d';
  };

  const getDangerColor = (danger: number) => {
    if (danger <= 2) return '#52c41a';
    if (danger <= 4) return '#faad14';
    if (danger <= 6) return '#fa8c16';
    if (danger <= 8) return '#f5222d';
    return '#a8071a';
  };

  const getTypeColor = (type: string) => {
    const colorMap = {
      compound: 'blue',
      isolation: 'green',
      cardio: 'orange',
    };
    return colorMap[type as keyof typeof colorMap] || 'default';
  };

  const getSymmetryColor = (mode: string) => {
    const colorMap = {
      symmetrical: 'blue',
      unilateral: 'purple',
      bilateral: 'cyan',
    };
    return colorMap[mode as keyof typeof colorMap] || 'default';
  };

  return (
    <Modal
      title={
        <Space>
          <TrophyOutlined />
          <Title level={3} style={{ margin: 0 }}>
            {exercise.name}
          </Title>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={900}
      bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
    >
      <Row gutter={[24, 24]}>
        {/* Basic Information */}
        <Col span={24}>
          <Card title="Basic Information" size="small">
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="Exercise Type">
                <Tag color={getTypeColor(exercise.type)}>
                  {exercise.type.toUpperCase()}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Symmetry Mode">
                <Tag color={getSymmetryColor(exercise.symmetryMode)}>
                  {exercise.symmetryMode}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Rep Range">
                <Text strong>{exercise.repRange[0]}-{exercise.repRange[1]} reps</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Sets">
                <Text>{exercise.sets || 'Not specified'}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Rest Time">
                <Space>
                  <ClockCircleOutlined />
                  <Text>{exercise.restTime ? `${exercise.restTime}s` : 'Not specified'}</Text>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Rating">
                <Rate disabled value={exercise.rating / 2} style={{ fontSize: '16px' }} />
                <Text style={{ marginLeft: 8 }}>({exercise.rating}/10)</Text>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        {/* Difficulty and Danger */}
        <Col span={12}>
          <Card title="Difficulty Level" size="small">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Progress
                percent={(exercise.difficulty / 10) * 100}
                strokeColor={getDifficultyColor(exercise.difficulty)}
                format={() => `${exercise.difficulty}/10`}
              />
              <Text type="secondary">
                {exercise.difficulty <= 3 && 'Beginner'}
                {exercise.difficulty > 3 && exercise.difficulty <= 6 && 'Intermediate'}
                {exercise.difficulty > 6 && exercise.difficulty <= 8 && 'Advanced'}
                {exercise.difficulty > 8 && 'Expert'}
              </Text>
            </Space>
          </Card>
        </Col>

        <Col span={12}>
          <Card title="Danger Level" size="small">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Space>
                <WarningOutlined style={{ color: getDangerColor(exercise.danger) }} />
                <Progress
                  percent={(exercise.danger / 10) * 100}
                  strokeColor={getDangerColor(exercise.danger)}
                  format={() => `${exercise.danger}/10`}
                />
              </Space>
              <Text type="secondary">
                {exercise.danger <= 2 && 'Very Low Risk'}
                {exercise.danger > 2 && exercise.danger <= 4 && 'Low Risk'}
                {exercise.danger > 4 && exercise.danger <= 6 && 'Moderate Risk'}
                {exercise.danger > 6 && exercise.danger <= 8 && 'High Risk'}
                {exercise.danger > 8 && 'Very High Risk'}
              </Text>
            </Space>
          </Card>
        </Col>

        {/* Muscle Activations */}
        <Col span={12}>
          <Card title="Muscle Activations" size="small">
            <List
              size="small"
              dataSource={Object.entries(exercise.muscleActivations)
                .sort(([, a], [, b]) => b - a)
                .slice(0, 8)}
              renderItem={([muscleId, activation]) => (
                <List.Item>
                  <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Text>{getMuscleGroupName(muscleId)}</Text>
                    <Space>
                      <Progress
                        percent={activation}
                        size="small"
                        showInfo={false}
                        strokeColor={activation >= 70 ? '#1890ff' : '#d9d9d9'}
                        style={{ width: 60 }}
                      />
                      <Text style={{ fontSize: '12px' }}>{activation}%</Text>
                    </Space>
                  </Space>
                </List.Item>
              )}
            />
          </Card>
        </Col>

        {/* Equipment Required */}
        <Col span={12}>
          <Card title="Equipment Required" size="small">
            {Object.keys(exercise.equipment).length > 0 ? (
              <List
                size="small"
                dataSource={Object.entries(exercise.equipment)}
                renderItem={([equipmentId, quantity]) => (
                  <List.Item>
                    <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                      <Text>{getEquipmentName(equipmentId)}</Text>
                      <Tag>{quantity}x</Tag>
                    </Space>
                  </List.Item>
                )}
              />
            ) : (
              <Text type="secondary">Bodyweight exercise - no equipment required</Text>
            )}
          </Card>
        </Col>

        {/* Target Adaptations */}
        <Col span={24}>
          <Card title="Target Adaptations" size="small">
            <Space wrap>
              {Object.entries(exercise.targetAdaptations)
                .sort(([, a], [, b]) => b - a)
                .map(([adaptation, value]) => (
                  <Card key={adaptation} size="small" style={{ width: 150 }}>
                    <Space direction="vertical" align="center">
                      {adaptation === 'strength' && <ThunderboltOutlined style={{ fontSize: 24, color: '#fa8c16' }} />}
                      {adaptation === 'hypertrophy' && <FireOutlined style={{ fontSize: 24, color: '#f5222d' }} />}
                      {adaptation === 'endurance' && <TrophyOutlined style={{ fontSize: 24, color: '#52c41a' }} />}
                      <Text strong>{adaptation.charAt(0).toUpperCase() + adaptation.slice(1)}</Text>
                      <Text type="secondary">{value}/10</Text>
                    </Space>
                  </Card>
                ))}
            </Space>
          </Card>
        </Col>

        {/* Instructions */}
        <Col span={12}>
          <Card title="Exercise Instructions" size="small">
            <List
              size="small"
              dataSource={exercise.instructions}
              renderItem={(instruction, index) => (
                <List.Item>
                  <Text>
                    <Text strong>{index + 1}. </Text>
                    {instruction}
                  </Text>
                </List.Item>
              )}
            />
          </Card>
        </Col>

        {/* Technique Checklist */}
        <Col span={12}>
          <Card title="Technique Checklist" size="small">
            <List
              size="small"
              dataSource={exercise.techniqueChecklist}
              renderItem={(item) => (
                <List.Item>
                  <Space>
                    <Text style={{ color: '#52c41a' }}>✓</Text>
                    <Text>{item}</Text>
                  </Space>
                </List.Item>
              )}
            />
          </Card>
        </Col>
      </Row>
    </Modal>
  );
};

export default ExerciseDetailsModal; 